import { gunzipSync } from 'fflate';
import { XMLParser } from 'fast-xml-parser';
import type { DetectedPlugin } from '../types';
import vst2IdsData from '../data/vst2Ids.json';

const vst2Ids = vst2IdsData as Record<
  string,
  { name: string; vendor?: string }
>;

/**
 * Convert a VST2 integer unique ID to its 4-character string representation (big-endian).
 */
function uniqueIdToChars(uniqueId: number): string {
  const chars = [
    (uniqueId >>> 24) & 0xff,
    (uniqueId >>> 16) & 0xff,
    (uniqueId >>> 8) & 0xff,
    uniqueId & 0xff,
  ];
  return chars
    .map((c) => (c >= 32 && c <= 126 ? String.fromCharCode(c) : '?'))
    .join('');
}

/**
 * Recursively walk an object and collect all VST/AU plugin information nodes.
 */
function collectPluginNodes(
  node: unknown,
  vst2Results: DetectedPlugin[],
  vst3Results: DetectedPlugin[],
  auResults: DetectedPlugin[]
): void {
  if (!node || typeof node !== 'object') return;

  if (Array.isArray(node)) {
    for (const item of node) {
      collectPluginNodes(item, vst2Results, vst3Results, auResults);
    }
    return;
  }

  const obj = node as Record<string, unknown>;

  // VST2 plugin info
  if ('VstPluginInfo' in obj) {
    const info = obj['VstPluginInfo'] as Record<string, unknown>;
    const plugName = (info['PlugName'] as Record<string, unknown>)?.[
      '@_Value'
    ] as string | undefined;
    const vendorString = (info['VendorString'] as Record<string, unknown>)?.[
      '@_Value'
    ] as string | undefined;
    const uniqueIdRaw = (info['UniqueId'] as Record<string, unknown>)?.[
      '@_Value'
    ];
    const uniqueId =
      uniqueIdRaw != null ? parseInt(String(uniqueIdRaw), 10) : null;

    if (plugName) {
      const vst2Id = uniqueId != null ? uniqueIdToChars(uniqueId) : undefined;
      const dbEntry = vst2Id ? vst2Ids[vst2Id] : undefined;

      vst2Results.push({
        name: dbEntry?.name ?? plugName,
        vendor: dbEntry?.vendor ?? vendorString,
        pluginType: 'VST2',
        vst2Id,
        confidence: dbEntry ? 0.97 : 0.85,
        detectionMethod: 'structured',
      });
    }
  }

  // VST3 plugin info
  if ('Vst3PluginInfo' in obj) {
    const info = obj['Vst3PluginInfo'] as Record<string, unknown>;
    const name = (info['Name'] as Record<string, unknown>)?.['@_Value'] as
      | string
      | undefined;
    const vendor = (info['Manufacturer'] as Record<string, unknown>)?.[
      '@_Value'
    ] as string | undefined;

    if (name) {
      vst3Results.push({
        name,
        vendor,
        pluginType: 'VST3',
        confidence: 0.97,
        detectionMethod: 'structured',
      });
    }
  }

  // AU plugin info
  if ('AuPluginInfo' in obj) {
    const info = obj['AuPluginInfo'] as Record<string, unknown>;
    const name = (info['Name'] as Record<string, unknown>)?.['@_Value'] as
      | string
      | undefined;
    const manufacturer = (info['Manufacturer'] as Record<string, unknown>)?.[
      '@_Value'
    ] as string | undefined;

    if (name) {
      auResults.push({
        name,
        vendor: manufacturer,
        pluginType: 'AU',
        confidence: 0.97,
        detectionMethod: 'structured',
      });
    }
  }

  // Recurse through all child keys
  for (const value of Object.values(obj)) {
    collectPluginNodes(value, vst2Results, vst3Results, auResults);
  }
}

/**
 * Parse Ableton Live Set (.als) file — gzip-compressed XML.
 * Returns all detected VST2, VST3, and AU plugin references.
 */
export function parseAbleton(bytes: Uint8Array): DetectedPlugin[] {
  // Decompress gzip
  const decompressed = gunzipSync(bytes);
  const xml = new TextDecoder('utf-8').decode(decompressed);

  return parseAbletonDocument(xml).plugins;
}

/** Shared XML/device traversal for projects and Blueprint presets/racks. */
export function parseAbletonDocument(xml: string) {
  const parser = new XMLParser({
    ignoreAttributes: false,
    attributeNamePrefix: '@_',
    parseAttributeValue: true,
    isArray: () => false, // We handle arrays manually in collectPluginNodes
  });

  const parsed = parser.parse(xml) as unknown;

  const vst2: DetectedPlugin[] = [];
  const vst3: DetectedPlugin[] = [];
  const au: DetectedPlugin[] = [];

  collectPluginNodes(parsed, vst2, vst3, au);

  return { document: parsed, plugins: [...vst2, ...vst3, ...au] };
}
